/**
 * The Activity tab's ADDRESS — `/dm/activity?origin=&status=&sprint=&agentType=&itemId=&liveness=`
 * (DM W2 A1).
 *
 * ── WHY THE FILTERS LIVE IN THE URL AND NOT IN THE COMPONENT ───────────────
 * `DmAgentRunPanel` held its one filter in `useState`, which was right while there was
 * one and it selected nothing an operator would want to send to somebody else. Six
 * filters over a ledger is a different object: "the runs of sprint #412 that have gone
 * stale" is a QUESTION, and a question an operator can only reach by six clicks is one
 * they cannot link to, cannot bookmark and cannot get back to after a refresh. The
 * address is the only place a view is shareable from, so it is the authority — the
 * component reads it and never holds a second copy
 * ([[feedback_shared_store_invariant_belongs_to_owner]]: one writer for one fact).
 *
 * ── THE URL KEY AND THE WIRE KEY ARE ALLOWED TO DIFFER, AND TWO DO ─────────
 * `?sprint=` is `sprintItemId` on the wire and `?itemId=` is `itemId`. The address is
 * read and typed by PEOPLE, and `?sprint=412` is what a person writes; the door's
 * parameter name is an API detail. The mapping is stated ONCE, in
 * {@link dmActivityListParams}, so there is exactly one place the two spellings meet —
 * a per-call-site translation is how a filter ends up in the address and never in the
 * request ([[feedback_wire_casing_projection_parity]]).
 *
 * ── EVERY CLOSED SET IS THE SHARED TUPLE, NEVER A COPY ────────────────────
 * `origin`, `status` and `liveness` are validated against `PM_AGENT_RUN_ORIGIN_VALUES`,
 * `PM_AGENT_RUN_STATUS_VALUES` and `DM_RUN_LIVENESS_VALUES` — the SAME declarations the
 * column's CHECK and the door's `z.enum` consume. A local copy of any of them would be a
 * second authority that starts refusing a value the database can already store
 * ([[feedback_cross_package_wire_discriminator_single_source]]), and it would fail in the
 * silent direction: an unrecognised word is dropped here, so the operator's narrowing
 * would quietly widen to "everything".
 *
 * ── AN UNPARSEABLE VALUE IS DROPPED, NOT CARRIED ──────────────────────────
 * The door is `.strict()` and refuses an out-of-vocabulary `?status=`, so forwarding a
 * bad word would turn a hand-edited address into a failed READ — a red panel where the
 * honest answer is "that is not a filter I have". A dropped key narrows nothing, which
 * is the safe direction here: the operator sees the full ledger and the control shows
 * "all", so the address and the screen agree.
 *
 * `?live=1` IS NOT A KEY THIS MODULE WRITES OR READS. The door refuses it with a 400 —
 * L3 chose the verdict's own vocabulary over a query-string boolean precisely because
 * `?live=false` coerces to `true`. It is named here so the next reader does not
 * reintroduce it from an older note.
 *
 * ── `?run=` IS AN ADDRESS MEMBER, NOT A SEVENTH AXIS (DM W2 A2, gh#2670) ───
 * It selects which run the INSPECTOR shows, and it is deliberately outside
 * {@link DmActivityFilter} rather than a field on it. The reason is mechanical: the list
 * read's IDENTITY is the serialised filter (`useDmAgentRuns`' `filterKeyOf`), so a `run`
 * that lived on the filter would change that identity and RE-READ THE WHOLE LIST every
 * time an operator opened, closed or switched the inspector — a page of fifty rows
 * re-fetched to draw a side panel, and the tree scrolling back to the top under them.
 *
 * So `run` gets its own parse/serialize pair below and never enters
 * {@link dmActivityListParams}. The two live in one module because they write one
 * address: {@link serializeDmActivitySearch} composes onto a base for exactly this
 * reason, and the tab's canonical rewrite is the composition of both serializers, which
 * is what keeps a rewrite from deleting the neighbour's key.
 *
 * No `@/` alias in this package; consumers use relative paths.
 */
import {
  DM_RUN_LIVENESS_VALUES,
  DM_RUN_PARENT_ROOT,
  PM_AGENT_RUN_ORIGIN_VALUES,
  PM_AGENT_RUN_STATUS_VALUES,
  type DmRunLiveness,
  type PmAgentRunOrigin,
  type PmAgentRunStatus,
} from '@elmes/shared';

/**
 * The Activity tab's filter, as the address carries it.
 *
 * `null` is the ABSENCE of a narrowing on that axis, never "empty string": the door is
 * `.strict()` and `?status=` with no value is a 400, so absence has to be expressible as
 * a value this object can hold and {@link dmActivityListParams} can drop.
 */
export interface DmActivityFilter {
  origin: PmAgentRunOrigin | null;
  status: PmAgentRunStatus | null;
  /** `?sprint=` — a `pm_item.id`, sent as `sprintItemId`. */
  sprintItemId: number | null;
  /** The operator's own agent roster; the server holds no list of it, so no enum. */
  agentType: string | null;
  itemId: number | null;
  liveness: DmRunLiveness | null;
}

/** No narrowing on any axis — the request `DmAgentRunPanel` has always sent. */
export const DM_ACTIVITY_FILTER_NONE: DmActivityFilter = {
  origin: null,
  status: null,
  sprintItemId: null,
  agentType: null,
  itemId: null,
  liveness: null,
};

/**
 * The address's key for each filter — the ONE place the URL spelling is written.
 *
 * A `Record<keyof DmActivityFilter, string>` rather than six literals scattered through
 * a parser and a serializer: the two halves have to agree about every key, and the way
 * they come to disagree is one of them being edited. Exported because the control panel
 * needs the key to build its `data-testid` and its `<label>` binding from the same
 * source the address uses.
 */
export const DM_ACTIVITY_URL_KEYS: Record<keyof DmActivityFilter, string> = {
  origin: 'origin',
  status: 'status',
  sprintItemId: 'sprint',
  agentType: 'agentType',
  itemId: 'itemId',
  liveness: 'liveness',
};

/**
 * The inspector's key — `?run=<pm_agent_run.id>`.
 *
 * Declared BESIDE {@link DM_ACTIVITY_URL_KEYS} rather than inside it, because that record
 * is typed `Record<keyof DmActivityFilter, string>` and putting `run` in it would require
 * putting `run` on the filter — which is exactly the re-read this module's header refuses.
 * One constant, so the parser, the serializer and the tab's ignored-pair sweep all spell
 * the key once.
 */
export const DM_ACTIVITY_RUN_KEY = 'run';

/**
 * The Activity tab's PATH, so a caller that navigates to it does not spell the route.
 *
 * It lives here rather than in a route registry because this module already owns the
 * SEARCH half of the same address, and a link built from a hand-typed path plus a
 * generated query is half-derived — the half nobody generated is the half that drifts.
 * The route's registrations read it too — the tab strip (`DmShellLayout`), the desktop
 * store's renamed-key descriptor and the windowable-page registry (dm.issue 1480).
 */
export const DM_ACTIVITY_ROUTE = '/dm/activity';

/** The axes, in the order the control strip renders them. */
export const DM_ACTIVITY_FILTER_AXES = [
  'origin',
  'status',
  'liveness',
  'agentType',
  'sprintItemId',
  'itemId',
] as const satisfies readonly (keyof DmActivityFilter)[];

/**
 * A member of `values`, or `null`.
 *
 * Generic over the TUPLE rather than over `string`, so the return type is the union the
 * caller's field declares and a mismatched pairing (reading `status` out of the origin
 * tuple) is a `tsc` error rather than a value that renders wrong.
 */
function memberOf<T extends string>(raw: string | null, values: readonly T[]): T | null {
  if (raw === null) return null;
  return (values as readonly string[]).includes(raw) ? (raw as T) : null;
}

/**
 * A POSITIVE integer id, or `null`.
 *
 * The door's own rule, mirrored rather than assumed: `?sprint=` coerces an empty value to
 * `0` there and is REFUSED, so `0`, a negative, a decimal and a non-number all have to drop
 * here — otherwise a hand-edited address turns into a 400 the operator reads as "the ledger
 * is broken".
 *
 * ── `Number`, BECAUSE THE DOOR USES `Number` ──────────────────────────────
 * `z.coerce.number()` is `Number(value)`, so mirroring the rule means using the SAME
 * coercion rather than a stricter-looking one. Two consequences are worth writing down
 * because they surprise:
 *
 *   * `Number.parseInt` is NOT used. It accepts `12abc` as `12`, so an address saying one
 *     thing and a request meaning another — the failure a dropped key does not have.
 *   * `0x1A` and `1e3` ARE accepted, as 26 and 1000. That is not an oversight and not a
 *     laxity this module chose: `Number` reads both, so the door reads both, and a parser
 *     that refused them here would drop a filter the server would have honoured — a
 *     narrowing silently widening to "everything", which is the direction this module's
 *     header calls out as the dangerous one. `dmActivityUrl.test.ts` pins them as
 *     ACCEPTED so the agreement is a decision on the record rather than a coincidence.
 */
function positiveId(raw: string | null): number | null {
  if (raw === null || raw.trim() === '') return null;
  const value = Number(raw);
  if (!Number.isInteger(value) || value <= 0) return null;
  return value;
}

/** A non-empty trimmed string, or `null` — the door's `.trim().min(1)`. */
function bounded(raw: string | null): string | null {
  if (raw === null) return null;
  const value = raw.trim();
  return value === '' ? null : value;
  // NO MAX LENGTH. `PM_AGENT_TYPE_MAX_LENGTH` is derived from the column and lives in the
  // API package, so a number here would be a hand-copied second authority over a width
  // this bundle cannot see. An over-long value is the server's 400 to give.
}

/** The address → the filter. Unrecognised values are DROPPED — see the header. */
export function parseDmActivitySearch(search: URLSearchParams): DmActivityFilter {
  return {
    origin: memberOf(search.get(DM_ACTIVITY_URL_KEYS.origin), PM_AGENT_RUN_ORIGIN_VALUES),
    status: memberOf(search.get(DM_ACTIVITY_URL_KEYS.status), PM_AGENT_RUN_STATUS_VALUES),
    sprintItemId: positiveId(search.get(DM_ACTIVITY_URL_KEYS.sprintItemId)),
    agentType: bounded(search.get(DM_ACTIVITY_URL_KEYS.agentType)),
    itemId: positiveId(search.get(DM_ACTIVITY_URL_KEYS.itemId)),
    liveness: memberOf(search.get(DM_ACTIVITY_URL_KEYS.liveness), DM_RUN_LIVENESS_VALUES),
  };
}

/**
 * The filter → the address, onto `base` so keys this module does not own survive.
 *
 * ── IT WRITES ONTO THE CURRENT SEARCH RATHER THAN REPLACING IT ────────────
 * A2 puts `?run=<id>` on this address and A4 puts `?sprint=`'s inspector beside it, so a
 * serializer that returned only its own six keys would delete the neighbour's the first
 * time a filter changed — the failure `DmLegacyRedirect` already documents for the board.
 * An absent filter DELETES its key rather than writing it empty, because the door refuses
 * an empty value and because a URL full of `?status=&origin=` is not the link an operator
 * meant to share.
 */
export function serializeDmActivitySearch(
  filter: DmActivityFilter,
  base: URLSearchParams = new URLSearchParams(),
): URLSearchParams {
  const next = new URLSearchParams(base);
  for (const axis of DM_ACTIVITY_FILTER_AXES) {
    const key = DM_ACTIVITY_URL_KEYS[axis];
    const value = filter[axis];
    if (value === null) next.delete(key);
    else next.set(key, String(value));
  }
  return next;
}

/**
 * The address → the run the inspector should show, or `null`.
 *
 * `positiveId`, the SAME rule the two id filters use and the same one the door applies to
 * `:id` — so `?run=0`, `?run=-1`, `?run=2.5` and `?run=bogus` all drop rather than
 * producing a request the server answers with a 400. A dropped `run` closes the inspector,
 * which is the safe direction: the tree is still there and the address is rewritten to say
 * so, where a forwarded bad value would paint a red panel over a working page.
 */
export function parseDmActivityRun(search: URLSearchParams): number | null {
  return positiveId(search.get(DM_ACTIVITY_RUN_KEY));
}

/**
 * The selected run → the address, onto `base` so the FILTER keys survive.
 *
 * The mirror of {@link serializeDmActivitySearch}'s composition rule, and both halves are
 * needed: each serializer writes only its own key and leaves everything else on the
 * `URLSearchParams` it was handed, so opening the inspector cannot drop a filter and
 * changing a filter cannot close the inspector.
 *
 * `null` DELETES the key rather than writing it empty — `?run=` coerces to `0` under the
 * rule above and would read as "not a run", so an empty value would be an address that
 * parses to the same view by accident rather than by design.
 */
export function serializeDmActivityRun(
  runId: number | null,
  base: URLSearchParams = new URLSearchParams(),
): URLSearchParams {
  const next = new URLSearchParams(base);
  if (runId === null) next.delete(DM_ACTIVITY_RUN_KEY);
  else next.set(DM_ACTIVITY_RUN_KEY, String(runId));
  return next;
}

/**
 * The filter → the door's query parameters, and the KEY SET is the contract.
 *
 * `/agent-runs` is `.strict()`, so "no filter" is expressed by OMITTING the key — never
 * by sending it `undefined`, which axios serialises away silently and which would make
 * this function's behaviour depend on the HTTP client rather than on this decision.
 *
 * This is also the ONE place `sprintItemId` and `sprint` meet: see the header.
 */
export function dmActivityListParams(
  filter: DmActivityFilter,
  rootsOnly = false,
): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  // ── THE ROOTS ASK, WHEN THE CALLER IS DRAWING A SESSION TREE ─────────────
  // `DM_RUN_PARENT_ROOT` is the door's word for `parent_run_id IS NULL`. It is the SAME
  // parameter the subtree read uses, so the two asks cannot both be sent — see that
  // constant on why this is one parameter with two arms rather than a second flag.
  if (rootsOnly) params.parentRunId = DM_RUN_PARENT_ROOT;
  if (filter.origin !== null) params.origin = filter.origin;
  if (filter.status !== null) params.status = filter.status;
  if (filter.sprintItemId !== null) params.sprintItemId = filter.sprintItemId;
  if (filter.agentType !== null) params.agentType = filter.agentType;
  if (filter.itemId !== null) params.itemId = filter.itemId;
  if (filter.liveness !== null) params.liveness = filter.liveness;
  return params;
}

/**
 * The narrowings in force, as `key=value` words — the ADDRESS's spelling, not the wire's.
 *
 * For the empty state, which has to name what it measured. "There are no agent runs" is
 * a claim about the TENANT that a filtered request cannot support; naming the axes is the
 * claim it CAN support, and using the URL keys means the sentence and the link an
 * operator would paste to a colleague say the same words.
 *
 * DERIVED from {@link DM_ACTIVITY_FILTER_AXES}, so a seventh filter appears in the
 * sentence without a second edit — a hand-listed version is how an empty state comes to
 * omit the one narrowing that caused the emptiness.
 */
export function describeDmActivityFilter(filter: DmActivityFilter): string[] {
  return DM_ACTIVITY_FILTER_AXES.filter((axis) => filter[axis] !== null).map(
    (axis) => `${DM_ACTIVITY_URL_KEYS[axis]}=${String(filter[axis])}`,
  );
}

/**
 * Whether the activity tree should ask for SESSION ROOTS rather than for runs.
 *
 * ── THE UNFILTERED TREE IS A TREE OF SESSIONS; A FILTERED ONE IS THE MATCHES ─
 * With nothing narrowing, the operator is reading the ledger's SHAPE — which sessions ran,
 * under which sprint — and a page that mixed parents and children would cut between them:
 * a newest-first window puts a child (created later) ahead of its parent, so a 50-row page
 * routinely contains sub-agents whose parents sit on page two. Asking for roots makes the
 * page a page of sessions, and the children arrive from the subtree read.
 *
 * With a filter in force the question changed. `?agentType=implementer` is about RUNS, and
 * almost every run it names is a sub-agent; answering it with roots only would return an
 * empty tree for a filter the ledger can satisfy. So the filter is sent as asked, matches
 * with no loaded parent are promoted to session level, and the tree stops claiming to be a
 * complete session list — which it no longer is.
 *
 * The body coincides with {@link isDmActivityFilterEmpty} today and is NOT an alias for it:
 * the two answer different questions ("does anything narrow" vs "is this a session view"),
 * and a filter that narrows without changing the subject — a project picker, say — would
 * separate them.
 */
export function dmActivityAsksRoots(filter: DmActivityFilter): boolean {
  return isDmActivityFilterEmpty(filter);
}

/** `true` when no axis narrows anything — the unfiltered ledger. */
export function isDmActivityFilterEmpty(filter: DmActivityFilter): boolean {
  return DM_ACTIVITY_FILTER_AXES.every((axis) => filter[axis] === null);
}

/**
 * The Activity page NARROWED TO ONE SPRINT — the address every "open this sprint" link
 * writes (DM W2 A4, gh#2671).
 *
 * ── WHY A HELPER AND NOT A TEMPLATE STRING AT EACH SITE ────────────────────
 * There are two call sites already — the run inspector's sprint field and, from A4, the
 * activity tree's sprint group — and both were about to spell `/dm/activity?sprint=${id}`
 * by hand. That is a link built from a hand-typed path plus a hand-typed key, i.e. the
 * half-derived address {@link DM_ACTIVITY_ROUTE}'s own docblock warns about: rename the
 * key in {@link DM_ACTIVITY_URL_KEYS} and every literal keeps compiling while pointing at
 * a filter that no longer exists.
 *
 * ── IT STARTS FROM `DM_ACTIVITY_FILTER_NONE`, WHICH IS THE POINT ───────────
 * The result is the sprint filter and NOTHING ELSE — not the caller's current filters,
 * not the run the inspector happens to be showing. A link that carried them would hand
 * the reader "this sprint, as filtered by wherever you were", which is a different
 * question from the one the link's text asks. Composing onto a base is what
 * {@link serializeDmActivitySearch} is for and is available to a caller that wants it.
 */
export function dmActivitySprintHref(sprintItemId: number): string {
  const search = serializeDmActivitySearch({ ...DM_ACTIVITY_FILTER_NONE, sprintItemId });
  return `${DM_ACTIVITY_ROUTE}?${search.toString()}`;
}

/**
 * The address that OPENS ONE RUN'S INSPECTOR — `dmActivitySprintHref`'s sibling (DM W2 A3).
 *
 * ── WHY IT EXISTS RATHER THAN A TEMPLATE STRING AT THE CALL SITE ───────────
 * The Board rail's live slice links every row to the run inspector, and it is on a
 * different route from this module's own surface — so it cannot compose onto a current
 * search the way the Activity tab does, and would otherwise spell
 * `/dm/activity?run=${id}` by hand. That is the half-derived address
 * {@link DM_ACTIVITY_ROUTE}'s docblock warns about: rename {@link DM_ACTIVITY_RUN_KEY} and
 * every literal keeps compiling while pointing at a key nothing parses.
 *
 * ── IT CARRIES THE RUN AND NOTHING ELSE ───────────────────────────────────
 * No filters, for {@link dmActivitySprintHref}'s reason: the link's text names ONE run, so
 * the address it produces has to be about that run rather than "that run, as filtered by
 * wherever the reader happened to be". A caller that wants composition passes a base to
 * {@link serializeDmActivityRun} directly, which is what the Activity tab's own rows do.
 */
export function dmActivityRunHref(runId: number): string {
  return `${DM_ACTIVITY_ROUTE}?${serializeDmActivityRun(runId).toString()}`;
}
